import { useCallback, useEffect, useState } from "react";
import {
  getServiceCostPreview,
  getServiceCostSnapshot,
  listServiceJobActualMaterials,
  listServiceTaggedRecipes,
  upsertServiceJobActualMaterial,
} from "@/lib/service/client";
import type {
  ServiceCostPreview,
  ServiceEvent,
  ServiceJobActualMaterial,
  ServiceJobCostSnapshot,
  ServiceTaggedRecipe,
} from "@/lib/service/types";

export function useServiceEventCost(event: ServiceEvent | null) {
  const [preview, setPreview] = useState<ServiceCostPreview | null>(null);
  const [snapshot, setSnapshot] = useState<ServiceJobCostSnapshot | null>(null);
  const [recipes, setRecipes] = useState<ServiceTaggedRecipe[]>([]);
  const [actualLines, setActualLines] = useState<ServiceJobActualMaterial[]>([]);
  const [recipeOverride, setRecipeOverride] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bomDraft, setBomDraft] = useState({
    label: "",
    quantity: "1",
    unit_cost_dollars: "",
    receipt_ref: "",
  });

  const reload = useCallback(async () => {
    if (!event) {
      setPreview(null);
      setSnapshot(null);
      setActualLines([]);
      return;
    }
    setBusy(true);
    setError(null);
    const recipeId = recipeOverride || undefined;
    const [previewRes, snapshotRes, recipesRes] = await Promise.all([
      getServiceCostPreview({ event_id: event.id, mfg_recipe_id: recipeId ?? null }),
      getServiceCostSnapshot(event.id),
      listServiceTaggedRecipes(),
    ]);
    let lines: ServiceJobActualMaterial[] = [];
    if (event.status === "complete") {
      const linesRes = await listServiceJobActualMaterials(event.id);
      if (!linesRes.ok) {
        setBusy(false);
        setError(linesRes.error);
        return;
      }
      lines = linesRes.data.lines;
    }
    setBusy(false);
    if (!previewRes.ok) {
      setError(previewRes.error);
      return;
    }
    if (!snapshotRes.ok) {
      setError(snapshotRes.error);
      return;
    }
    if (!recipesRes.ok) {
      setError(recipesRes.error);
      return;
    }
    setPreview(previewRes.data.preview);
    setSnapshot(snapshotRes.data.snapshot);
    setRecipes(recipesRes.data.recipes);
    setActualLines(lines);
  }, [event, recipeOverride]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    setRecipeOverride("");
  }, [event?.id]);

  const addBomLine = useCallback(async () => {
    if (!event || event.status !== "complete") return;
    const label = bomDraft.label.trim();
    if (!label) {
      setError("Material label is required.");
      return;
    }
    const quantity = Number(bomDraft.quantity);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setError("Quantity must be positive.");
      return;
    }
    const unitDollars = bomDraft.unit_cost_dollars.trim()
      ? Number(bomDraft.unit_cost_dollars)
      : null;
    if (unitDollars != null && (!Number.isFinite(unitDollars) || unitDollars < 0)) {
      setError("Unit cost must be a valid dollar amount.");
      return;
    }
    setActionBusy(true);
    setError(null);
    const res = await upsertServiceJobActualMaterial({
      event_id: event.id,
      label,
      quantity,
      unit_cost_cents: unitDollars != null ? Math.round(unitDollars * 100) : null,
      source: bomDraft.receipt_ref.trim() ? "receipt" : "manual",
      receipt_ref: bomDraft.receipt_ref.trim() || null,
    });
    setActionBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setBomDraft({ label: "", quantity: "1", unit_cost_dollars: "", receipt_ref: "" });
    await reload();
  }, [bomDraft, event, reload]);

  const removeBomLine = useCallback(
    async (lineId: string) => {
      if (!event) return;
      setActionBusy(true);
      const res = await upsertServiceJobActualMaterial({
        event_id: event.id,
        id: lineId,
        delete: true,
      });
      setActionBusy(false);
      if (!res.ok) {
        setError(res.error);
        return;
      }
      await reload();
    },
    [event, reload]
  );

  return {
    preview,
    snapshot,
    recipes,
    actualLines,
    recipeOverride,
    setRecipeOverride,
    busy,
    actionBusy,
    error,
    bomDraft,
    setBomDraft,
    addBomLine,
    removeBomLine,
    reload,
  };
}
